import { Link, createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { EmailNotVerifiedError, prefetchSocialUrl, signInEmail } from '~/lib/auth';
import { buildHead } from '~/seo/site';

export const Route = createFileRoute('/auth/sign-in')({
  head: () =>
    buildHead({
      title: 'Sign In',
      description: 'Sign in to Prod Battle with email, Google, or Discord.',
      path: '/auth/sign-in',
      noindex: true,
    }),
  component: SignIn,
});

function SignIn() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [oauthErr, setOauthErr] = useState<string | null>(null);
  const [oauthBusy, setOauthBusy] = useState<'google' | 'discord' | null>(null);

  async function startSocial(provider: 'google' | 'discord') {
    setOauthErr(null);
    setOauthBusy(provider);
    try {
      const url = await prefetchSocialUrl(provider, `${window.location.origin}/play`);
      window.location.href = url;
    } catch (e) {
      setOauthErr((e as Error).message);
      setOauthBusy(null);
    }
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setPending(true);
    try {
      await signInEmail({ email, password });
      // Full page navigation so the root layout's useSession re-mounts
      // and the header flips to authed state.
      window.location.assign('/play');
    } catch (e2) {
      if (e2 instanceof EmailNotVerifiedError) {
        setErr('Check your email first - we sent you a verification link.');
      } else {
        setErr((e2 as Error).message);
      }
    } finally {
      setPending(false);
    }
  }

  return (
    <section className="mx-auto max-w-md px-4 py-16 space-y-6">
      <header>
        <h2 className="text-2xl font-bold">Sign in</h2>
        <p className="text-muted-foreground text-sm">
          No account?{' '}
          <Link to="/auth/sign-up" className="underline">
            Create one.
          </Link>
        </p>
      </header>

      <div className="grid gap-2">
        <button
          type="button"
          onClick={() => startSocial('google')}
          disabled={oauthBusy !== null}
          className="w-full rounded-md border border-border bg-background px-4 py-2 text-sm font-medium hover:bg-accent disabled:opacity-60"
        >
          {oauthBusy === 'google' ? 'Opening Google...' : 'Continue with Google'}
        </button>
        <button
          type="button"
          onClick={() => startSocial('discord')}
          disabled={oauthBusy !== null}
          className="w-full rounded-md border border-border bg-[#5865F2] px-4 py-2 text-sm font-medium text-white hover:bg-[#4752C4] disabled:opacity-60"
        >
          {oauthBusy === 'discord' ? 'Opening Discord...' : 'Continue with Discord'}
        </button>
        {oauthErr && <p className="text-xs text-red-500 text-center">{oauthErr}</p>}
      </div>

      <p className="text-center text-xs text-muted-foreground">
        By continuing with Google or Discord, you agree to our{' '}
        <Link to="/legal" hash="terms" className="underline">
          Terms of Service
        </Link>{' '}
        and{' '}
        <Link to="/legal" hash="privacy" className="underline">
          Privacy Policy
        </Link>
        .
      </p>

      <div className="flex items-center gap-3 text-xs text-muted-foreground">
        <div className="h-px flex-1 bg-border" />
        or
        <div className="h-px flex-1 bg-border" />
      </div>

      <form onSubmit={onSubmit} className="space-y-3">
        <label className="block text-sm">
          <span className="mb-1 block text-muted-foreground">Email</span>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-md border border-border bg-background px-3 py-2"
          />
        </label>
        <label className="block text-sm">
          <div className="mb-1 flex items-baseline justify-between">
            <span className="text-muted-foreground">Password</span>
            <Link
              to="/auth/forgot-password"
              className="text-xs text-muted-foreground underline hover:text-foreground"
            >
              Forgot password?
            </Link>
          </div>
          <input
            type="password"
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-md border border-border bg-background px-3 py-2"
          />
        </label>
        {err && <p className="text-sm text-red-500">{err}</p>}
        <button
          type="submit"
          disabled={pending}
          className="w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
        >
          {pending ? 'Signing in...' : 'Sign in'}
        </button>
      </form>
    </section>
  );
}
